import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  Alert,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notes as NotesIcon,
  History as HistoryIcon,
  Timeline as TimelineIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import api from '../services/api';
import type { Scenario, Execution, ExecutionLogEntry } from '../types/attacks';
import { ExecutionStatusColors } from '../types/attacks';
import type { RunbookListItem } from '../types/runbooks';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import { PageTransition } from '../components/PageTransition';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatUiDateOnly, formatUiDateTime, formatUiTimeWithSeconds } from '../utils/dateUtils';
import { executionMetricsRange } from '../utils/executionMetricsRange';
import { describeResourceCaps, hasResourceCaps } from '../utils/resourceCaps';
import MetricsPanel from '../components/operations/MetricsPanel';
import { useExecutionCursorSync } from '../hooks/useExecutionCursorSync';
import ExecutionBotIpsPanel from '../components/operations/ExecutionBotIpsPanel';
import OperationalErrorsPanel from '../components/operations/OperationalErrorsPanel';
import {
  useCanViewOperationalErrors,
  useOperationalErrors,
} from '../hooks/useOperationalErrors';

const EXECUTIONS_ERRORS_COLLAPSED_KEY = 'executions.errors.collapsed.v1';
const EXECUTION_DETAILS_SPLIT_PCT_KEY = 'executions.details.logMetricsSplitPct.v1';
const EXECUTION_DETAILS_SHOW_SUMMARY_KEY = 'executions.details.showSummary.v1';
const EXECUTION_DETAILS_SHOW_LOG_KEY = 'executions.details.showLog.v1';
const EXECUTION_DETAILS_SHOW_METRICS_KEY = 'executions.details.showMetrics.v1';
const LOG_METRICS_DIVIDER_PX = 10;

function readStoredLogMetricsSplitPct(): number {
  try {
    const v = localStorage.getItem(EXECUTION_DETAILS_SPLIT_PCT_KEY);
    const n = v ? parseFloat(v) : NaN;
    if (Number.isFinite(n) && n >= 20 && n <= 80) return n;
  } catch {
    /* ignore */
  }
  return 38;
}

function readStoredBool(key: string, defaultValue: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch {
    /* ignore */
  }
  return defaultValue;
}

function durationMs(start: string | undefined, end: string | undefined): number | null {
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return b - a;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatLogEventType(eventType: string): string {
  const map: Record<string, string> = {
    activate: 'Activate scenario',
    start: 'Start attack',
    start_failed: 'Start failed',
    pause: 'Pause',
    termination: 'Termination',
    scale: 'Scale',
    config_change: 'Config change',
  };
  return map[eventType] ?? eventType;
}

/** Stable hue from full user key so different users get different colors even if the same initial. */
function userKeyHue(key: string): number {
  let h = 0;
  const s = key || '';
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

function userAvatarColors(userKey: string, isDark: boolean): { bg: string; fg: string } {
  const hue = userKeyHue(userKey);
  if (isDark) {
    return {
      bg: `hsl(${hue} 58% 36%)`,
      fg: `hsl(${hue} 15% 96%)`,
    };
  }
  return {
    bg: `hsl(${hue} 52% 44%)`,
    fg: `hsl(${hue} 10% 99%)`,
  };
}

function userInitialFromEmail(email: string | null | undefined): string {
  if (!email?.trim()) return '';
  const local = email.trim().split('@')[0] || email.trim();
  const c = local.charAt(0);
  return c ? c.toUpperCase() : '';
}

function logUserInitial(entry: ExecutionLogEntry): string {
  const fromEmail = userInitialFromEmail(entry.user_email);
  if (fromEmail) return fromEmail;
  const id = entry.user_id?.trim();
  if (id) {
    const c = id.charAt(0);
    return /[a-zA-Z0-9]/.test(c) ? c.toUpperCase() : '?';
  }
  return '?';
}

function logUserKey(entry: ExecutionLogEntry): string {
  return (entry.user_email || entry.user_id || '').trim() || '?';
}

function logUserTooltip(entry: ExecutionLogEntry): string {
  return entry.user_email?.trim() || entry.user_id?.trim() || 'Unknown';
}

function parseExecutionLogTimestampMs(iso: string): number | null {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function getLogEntryDescription(entry: ExecutionLogEntry): string {
  const d = entry.details as Record<string, unknown> | undefined;
  if (entry.event_type === 'activate') {
    return d?.scenario_name ? `Scenario "${String(d.scenario_name)}" activated` : 'Scenario activated';
  }
  if (entry.event_type === 'start') return 'Execution started';
  if (entry.event_type === 'start_failed' && d?.reason) return `Start attempt failed: ${String(d.reason)}`;
  if (entry.event_type === 'start_failed') return 'Start attempt failed';
  if (entry.event_type === 'pause') return 'Attack paused';
  if (entry.event_type === 'termination' && d) {
    if (d.reason) return String(d.reason);
    if (d.status) return `Status: ${d.status}`;
  }
  if (!d) return '';
  if (entry.event_type === 'scale') {
    const parts: string[] = [];
    const requested = d.requested_amount != null ? Number(d.requested_amount) : null;
    const created = d.bots_created != null ? Number(d.bots_created) : null;
    if (requested != null && created != null && created < requested) {
      parts.push(`Requested ${requested}, created ${created}`);
    }
    if (d.threads != null) parts.push(`threads: ${d.threads}`);
    if (d.bots_created != null && Number(d.bots_created) > 0) parts.push(`+${d.bots_created} created`);
    if (d.bots_started != null && Number(d.bots_started) > 0) parts.push(`+${d.bots_started} started`);
    if (d.bots_shutdown != null && Number(d.bots_shutdown) > 0) parts.push(`−${d.bots_shutdown} shutdown`);
    if (d.regions != null && Array.isArray(d.regions) && (d.regions as string[]).length > 0) {
      parts.push(`regions: ${(d.regions as string[]).join(', ')}`);
    }
    return parts.join(' · ') || '—';
  }
  if (entry.event_type === 'config_change') {
    if (d.updated_fields != null) {
      const uf = d.updated_fields;
      return `Fields: ${Array.isArray(uf) ? (uf as string[]).join(', ') : String(uf)}`;
    }
    if (d.threads_new != null || d.threads_previous != null) {
      return `threads: ${d.threads_previous ?? '—'} → ${d.threads_new ?? '—'}`;
    }
    return 'Configuration updated';
  }
  return '';
}

type ExecutionSortKey = 'started_at' | 'completed_at' | 'status' | 'impact_bots';
type ScenarioSortKey = 'name' | 'attack_type' | 'execution_count' | 'last_execution_at';

interface ExecutionsCollapsibleSectionProps {
  title: string;
  /** One-line hint when the section is collapsed */
  summaryCollapsed?: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  mb?: number;
}

const ExecutionsCollapsibleSection: React.FC<ExecutionsCollapsibleSectionProps> = ({
  title,
  summaryCollapsed,
  expanded,
  onToggle,
  children,
  mb = 2,
}) => {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        p: 2,
        mb,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(12px)',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={onToggle}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          {!expanded && summaryCollapsed ? (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }} noWrap>
              {summaryCollapsed}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse section' : 'Expand section'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ pt: 1.5 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

const Executions: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { timezone } = useTimezone();
  const { selectedTenant } = useTenant();
  const { showNotification } = useNotification();

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [runbooks, setRunbooks] = useState<RunbookListItem[]>([]);
  const [runbooksLoading, setRunbooksLoading] = useState(false);
  const [runbookFilterId, setRunbookFilterId] = useState<string>('');
  const [scenarioSearch, setScenarioSearch] = useState('');

  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [executionDetail, setExecutionDetail] = useState<Execution | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  /**
   * One reference time shared by every metrics chart: hovering a log row moves it,
   * clicking a log row or a point on any chart locks it there.
   */
  const cursor = useExecutionCursorSync();

  const [sortBy, setSortBy] = useState<ExecutionSortKey>('started_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [scenarioSortBy, setScenarioSortBy] = useState<ScenarioSortKey>('last_execution_at');
  const [scenarioSortDir, setScenarioSortDir] = useState<'asc' | 'desc'>('desc');

  const [impactBotsEdit, setImpactBotsEdit] = useState<string>('');
  const [savingImpactBots, setSavingImpactBots] = useState(false);
  const [configSnapshotExpanded, setConfigSnapshotExpanded] = useState(false);

  const [scenarioSectionOpen, setScenarioSectionOpen] = useState(true);
  const [executionSectionOpen, setExecutionSectionOpen] = useState(true);
  const [detailsSectionOpen, setDetailsSectionOpen] = useState(true);

  const [logMetricsSplitPct, setLogMetricsSplitPct] = useState(readStoredLogMetricsSplitPct);
  const [showExecutionSummaryPanel, setShowExecutionSummaryPanel] = useState(() =>
    readStoredBool(EXECUTION_DETAILS_SHOW_SUMMARY_KEY, true)
  );
  const [showExecutionLogPanel, setShowExecutionLogPanel] = useState(() =>
    readStoredBool(EXECUTION_DETAILS_SHOW_LOG_KEY, true)
  );
  const [showExecutionMetricsPanel, setShowExecutionMetricsPanel] = useState(() =>
    readStoredBool(EXECUTION_DETAILS_SHOW_METRICS_KEY, true)
  );
  const splitContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(EXECUTION_DETAILS_SPLIT_PCT_KEY, String(logMetricsSplitPct));
    } catch {
      /* ignore */
    }
  }, [logMetricsSplitPct]);

  useEffect(() => {
    try {
      localStorage.setItem(EXECUTION_DETAILS_SHOW_SUMMARY_KEY, showExecutionSummaryPanel ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [showExecutionSummaryPanel]);

  useEffect(() => {
    try {
      localStorage.setItem(EXECUTION_DETAILS_SHOW_LOG_KEY, showExecutionLogPanel ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [showExecutionLogPanel]);

  useEffect(() => {
    try {
      localStorage.setItem(EXECUTION_DETAILS_SHOW_METRICS_KEY, showExecutionMetricsPanel ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [showExecutionMetricsPanel]);

  useEffect(() => {
    cursor.clear();
  }, [selectedExecutionId, executionDetail?.id, cursor.clear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showExecutionLogPanel) cursor.clear();
  }, [showExecutionLogPanel, cursor.clear]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExecutionLogPanel = useCallback(() => {
    setShowExecutionLogPanel((log) => {
      if (!log) return true;
      setShowExecutionMetricsPanel((m) => (!m ? true : m));
      return false;
    });
  }, []);

  const toggleExecutionSummaryPanel = useCallback(() => {
    setShowExecutionSummaryPanel((v) => !v);
  }, []);

  const toggleExecutionMetricsPanel = useCallback(() => {
    setShowExecutionMetricsPanel((m) => {
      if (!m) return true;
      setShowExecutionLogPanel((l) => (!l ? true : l));
      return false;
    });
  }, []);

  const onLogMetricsDividerDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const el = splitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = Math.max(1, rect.width - LOG_METRICS_DIVIDER_PX);
      const startX = e.clientX;
      const startPct = logMetricsSplitPct;
      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const deltaPct = (dx / total) * 100;
        setLogMetricsSplitPct(Math.min(80, Math.max(20, startPct + deltaPct)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [logMetricsSplitPct]
  );

  const loadScenarios = useCallback(async () => {
    if (!selectedTenant?.tenant_id) return;
    setScenariosLoading(true);
    try {
      const data = await api.getScenarios(runbookFilterId || undefined);
      setScenarios(data);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
      setScenarios([]);
    } finally {
      setScenariosLoading(false);
    }
  }, [selectedTenant?.tenant_id, runbookFilterId]);

  const loadRunbooks = useCallback(async () => {
    if (!selectedTenant?.tenant_id) return;
    setRunbooksLoading(true);
    try {
      const data = await api.getRunbooks();
      setRunbooks(data);
    } catch (err) {
      console.error('Failed to load runbooks:', err);
      setRunbooks([]);
    } finally {
      setRunbooksLoading(false);
    }
  }, [selectedTenant?.tenant_id]);

  useEffect(() => {
    if (!selectedTenant) {
      setScenarios([]);
      setRunbooks([]);
      setSelectedScenario(null);
      setExecutions([]);
      setSelectedExecutionId(null);
      setExecutionDetail(null);
      setRunbookFilterId('');
      return;
    }
    loadRunbooks();
  }, [selectedTenant, loadRunbooks]);

  useEffect(() => {
    if (!selectedTenant) return;
    loadScenarios();
  }, [selectedTenant, loadScenarios]);

  // Persistent operational-error feed for the tenant. The executions already loaded above feed the
  // execution-error source, so the panel adds no extra /attacks/executions request.
  const canViewOperationalErrors = useCanViewOperationalErrors(selectedTenant?.tenant_id);
  const operationalErrors = useOperationalErrors({
    tenantId: selectedTenant?.tenant_id,
    scenarioId: selectedScenario?.id,
    executions,
    enabled: canViewOperationalErrors,
  });
  const [errorsCollapsed, setErrorsCollapsed] = useState(() =>
    readStoredBool(EXECUTIONS_ERRORS_COLLAPSED_KEY, false)
  );

  const toggleErrorsCollapsed = useCallback(() => {
    setErrorsCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(EXECUTIONS_ERRORS_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!selectedScenario?.id) {
      setExecutions([]);
      setSelectedExecutionId(null);
      setExecutionDetail(null);
      return;
    }
    let cancelled = false;
    setExecutionsLoading(true);
    setSelectedExecutionId(null);
    setExecutionDetail(null);
    api
      .getExecutions(selectedScenario.id)
      .then((data) => {
        if (!cancelled) setExecutions(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load executions:', err);
          setExecutions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setExecutionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedScenario?.id]);

  useEffect(() => {
    if (!selectedExecutionId) {
      setExecutionDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setConfigSnapshotExpanded(false);
    api
      .getExecution(selectedExecutionId)
      .then((detail) => {
        if (!cancelled) {
          setExecutionDetail(detail);
          setImpactBotsEdit(detail.impact_bots != null ? String(detail.impact_bots) : '');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load execution detail:', err);
          showNotification('Failed to load execution detail.', 'error');
          setExecutionDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedExecutionId, showNotification]);

  const handleSaveImpactBots = useCallback(async () => {
    if (!executionDetail?.id) return;
    const num = impactBotsEdit.trim() === '' ? null : parseInt(impactBotsEdit.trim(), 10);
    if (num !== null && (Number.isNaN(num) || num < 0)) {
      showNotification('Please enter a non-negative number.', 'error');
      return;
    }
    setSavingImpactBots(true);
    try {
      const updated = await api.updateExecutionImpactBots(executionDetail.id, num);
      setExecutionDetail(updated);
      setExecutions((prev) => prev.map((e) => (e.id === updated.id ? { ...e, impact_bots: updated.impact_bots } : e)));
      showNotification('Impact Bots updated.', 'success');
    } catch (err) {
      console.error('Failed to update impact bots:', err);
      showNotification('Failed to update Impact Bots.', 'error');
    } finally {
      setSavingImpactBots(false);
    }
  }, [executionDetail, impactBotsEdit, showNotification]);

  const getStatusColor = (status: string) => {
    const key = status as keyof typeof ExecutionStatusColors;
    return (ExecutionStatusColors[key] ?? 'default') as
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning';
  };

  const filteredScenarios = useMemo(() => {
    const q = scenarioSearch.trim().toLowerCase();
    if (!q) return scenarios;
    return scenarios.filter((s) => {
      const name = (s.name || '').toLowerCase();
      const desc = (s.description || '').toLowerCase();
      const at = (s.attack_type?.name || '').toLowerCase();
      const rb = (s.runbooks || []).some((r) => r.name.toLowerCase().includes(q));
      return name.includes(q) || desc.includes(q) || at.includes(q) || rb;
    });
  }, [scenarios, scenarioSearch]);

  const sortedScenarios = useMemo(() => {
    const arr = [...filteredScenarios];
    const dir = scenarioSortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      switch (scenarioSortBy) {
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'attack_type':
          cmp = (a.attack_type?.name || '').localeCompare(b.attack_type?.name || '');
          break;
        case 'execution_count':
          cmp = (a.execution_count ?? 0) - (b.execution_count ?? 0);
          break;
        case 'last_execution_at': {
          const ta = a.last_execution_at ? new Date(a.last_execution_at).getTime() : 0;
          const tb = b.last_execution_at ? new Date(b.last_execution_at).getTime() : 0;
          cmp = ta - tb;
          break;
        }
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * dir;
      return (a.name || '').localeCompare(b.name || '') * dir;
    });
    return arr;
  }, [filteredScenarios, scenarioSortBy, scenarioSortDir]);

  const sortedExecutions = useMemo(() => {
    const arr = [...executions];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'started_at': {
          const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
          const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
          cmp = ta - tb;
          break;
        }
        case 'completed_at': {
          const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
          const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
          cmp = ta - tb;
          break;
        }
        case 'status':
          cmp = (a.status || '').localeCompare(b.status || '');
          break;
        case 'impact_bots': {
          const ia = a.impact_bots ?? -1;
          const ib = b.impact_bots ?? -1;
          cmp = ia - ib;
          break;
        }
        default:
          cmp = 0;
      }
      return cmp * dir;
    });
    return arr;
  }, [executions, sortBy, sortDir]);

  const toggleSort = (key: ExecutionSortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'started_at' || key === 'completed_at' ? 'desc' : 'asc');
    }
  };

  const toggleScenarioSort = (key: ScenarioSortKey) => {
    if (scenarioSortBy === key) {
      setScenarioSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setScenarioSortBy(key);
      setScenarioSortDir(key === 'last_execution_at' || key === 'execution_count' ? 'desc' : 'asc');
    }
  };

  const metricsRange = executionDetail ? executionMetricsRange(executionDetail) : null;

  const scenarioSectionSummary = useMemo(() => {
    if (selectedScenario) {
      return `Selected: ${selectedScenario.name}`;
    }
    const n = sortedScenarios.length;
    return `${n} scenario${n === 1 ? '' : 's'}${scenarioSearch.trim() || runbookFilterId ? ' (filtered)' : ''}`;
  }, [selectedScenario, sortedScenarios.length, scenarioSearch, runbookFilterId]);

  const executionSectionSummary = useMemo(() => {
    if (!selectedScenario) return '';
    if (executionsLoading) return 'Loading…';
    if (sortedExecutions.length === 0) return 'No executions';
    if (!selectedExecutionId) {
      return `${sortedExecutions.length} execution${sortedExecutions.length === 1 ? '' : 's'} — pick one`;
    }
    const ex = sortedExecutions.find((e) => e.id === selectedExecutionId);
    if (!ex) return 'Execution selected';
    const started = ex.started_at ? formatUiDateTime(ex.started_at, timezone) : '—';
    return `${started} · ${ex.status}`;
  }, [
    selectedScenario,
    executionsLoading,
    sortedExecutions,
    selectedExecutionId,
    timezone,
  ]);

  const detailsSectionSummary = useMemo(() => {
    if (!selectedExecutionId) return 'No execution selected';
    if (detailLoading) return 'Loading…';
    if (!executionDetail) return '—';
    const started = executionDetail.started_at
      ? formatUiDateTime(executionDetail.started_at, timezone)
      : '—';
    return `${executionDetail.status} · ${started}`;
  }, [selectedExecutionId, detailLoading, executionDetail, timezone]);

  return (
    <PageTransition>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Executions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a scenario, then an execution, to view logs and metrics for that run.
        </Typography>

        {canViewOperationalErrors && (
          <Box sx={{ mb: 2 }}>
            <OperationalErrorsPanel
              variant="card"
              isCollapsed={errorsCollapsed}
              onToggle={toggleErrorsCollapsed}
              errors={operationalErrors.errors}
              loading={operationalErrors.loading}
              error={operationalErrors.error}
              degradedSources={operationalErrors.degradedSources}
              lastUpdatedMs={operationalErrors.lastUpdatedMs}
              onRefresh={operationalErrors.refresh}
            />
          </Box>
        )}

        {/* Stage 1: scenario selection */}
        <ExecutionsCollapsibleSection
          title="1. Scenario"
          summaryCollapsed={scenarioSectionSummary}
          expanded={scenarioSectionOpen}
          onToggle={() => setScenarioSectionOpen((o) => !o)}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Search scenarios"
                placeholder="Name, description, attack type, or runbook"
                value={scenarioSearch}
                onChange={(e) => setScenarioSearch(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="executions-runbook-filter">Filter by runbook</InputLabel>
                <Select
                  labelId="executions-runbook-filter"
                  label="Filter by runbook"
                  value={runbookFilterId}
                  onChange={(e) => {
                    setRunbookFilterId(e.target.value as string);
                    setSelectedScenario(null);
                  }}
                  disabled={runbooksLoading}
                >
                  <MenuItem value="">
                    <em>All runbooks</em>
                  </MenuItem>
                  {runbooks.map((rb) => (
                    <MenuItem key={rb.id} value={rb.id}>
                      {rb.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {scenariosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={scenarioSortBy === 'name' ? scenarioSortDir : false}>
                      <TableSortLabel
                        active={scenarioSortBy === 'name'}
                        direction={scenarioSortBy === 'name' ? scenarioSortDir : 'asc'}
                        onClick={() => toggleScenarioSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={scenarioSortBy === 'attack_type' ? scenarioSortDir : false}>
                      <TableSortLabel
                        active={scenarioSortBy === 'attack_type'}
                        direction={scenarioSortBy === 'attack_type' ? scenarioSortDir : 'asc'}
                        onClick={() => toggleScenarioSort('attack_type')}
                      >
                        Attack type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={scenarioSortBy === 'last_execution_at' ? scenarioSortDir : false}>
                      <TableSortLabel
                        active={scenarioSortBy === 'last_execution_at'}
                        direction={scenarioSortBy === 'last_execution_at' ? scenarioSortDir : 'asc'}
                        onClick={() => toggleScenarioSort('last_execution_at')}
                      >
                        Last execution
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Runbooks</TableCell>
                    <TableCell sortDirection={scenarioSortBy === 'execution_count' ? scenarioSortDir : false} align="right">
                      <TableSortLabel
                        active={scenarioSortBy === 'execution_count'}
                        direction={scenarioSortBy === 'execution_count' ? scenarioSortDir : 'asc'}
                        onClick={() => toggleScenarioSort('execution_count')}
                        sx={{ justifyContent: 'flex-end', width: '100%' }}
                      >
                        Executions
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedScenarios.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No scenarios match your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedScenarios.map((s) => {
                      const selected = selectedScenario?.id === s.id;
                      return (
                        <TableRow
                          key={s.id}
                          hover
                          selected={selected}
                          onClick={() => setSelectedScenario(s)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={selected ? 600 : 400}>
                              {s.name}
                            </Typography>
                            {s.description ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {s.description}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {s.attack_type ? (
                              <Chip label={s.attack_type.name} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {s.last_execution_at ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
                                <Typography variant="body2">{formatUiDateOnly(s.last_execution_at, timezone)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatUiTimeWithSeconds(s.last_execution_at, timezone)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {(s.runbooks || []).length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(s.runbooks || []).map((rb) => (
                                  <Chip key={rb.id} label={rb.name} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">{s.execution_count ?? 0}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ExecutionsCollapsibleSection>

        {!selectedScenario && (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Typography color="text.secondary">Select a scenario above to load executions.</Typography>
          </Paper>
        )}

        {selectedScenario && (
          <>
            <ExecutionsCollapsibleSection
              title={`2. Execution — ${selectedScenario.name}`}
              summaryCollapsed={executionSectionSummary}
              expanded={executionSectionOpen}
              onToggle={() => setExecutionSectionOpen((o) => !o)}
            >
              {executionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : sortedExecutions.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                  No executions for this scenario.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={sortBy === 'started_at' ? sortDir : false}>
                          <TableSortLabel
                            active={sortBy === 'started_at'}
                            direction={sortBy === 'started_at' ? sortDir : 'asc'}
                            onClick={() => toggleSort('started_at')}
                          >
                            Started
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'completed_at' ? sortDir : false}>
                          <TableSortLabel
                            active={sortBy === 'completed_at'}
                            direction={sortBy === 'completed_at' ? sortDir : 'asc'}
                            onClick={() => toggleSort('completed_at')}
                          >
                            Completed
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                          <TableSortLabel
                            active={sortBy === 'status'}
                            direction={sortBy === 'status' ? sortDir : 'asc'}
                            onClick={() => toggleSort('status')}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'impact_bots' ? sortDir : false} align="right">
                          <TableSortLabel
                            active={sortBy === 'impact_bots'}
                            direction={sortBy === 'impact_bots' ? sortDir : 'asc'}
                            onClick={() => toggleSort('impact_bots')}
                            sx={{ justifyContent: 'flex-end', width: '100%' }}
                          >
                            Impact bots
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedExecutions.map((ex) => {
                        const dur = durationMs(ex.started_at, ex.completed_at);
                        const rowSel = selectedExecutionId === ex.id;
                        return (
                          <TableRow
                            key={ex.id}
                            hover
                            selected={rowSel}
                            onClick={() => setSelectedExecutionId(ex.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{formatUiDateTime(ex.started_at, timezone)}</TableCell>
                            <TableCell>{formatUiDateTime(ex.completed_at, timezone)}</TableCell>
                            <TableCell>{dur !== null ? formatDuration(dur) : '—'}</TableCell>
                            <TableCell>
                              <Chip label={ex.status} size="small" color={getStatusColor(ex.status)} variant="filled" />
                            </TableCell>
                            <TableCell align="right">{ex.impact_bots ?? '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </ExecutionsCollapsibleSection>

            <ExecutionsCollapsibleSection
              title="3. Logs and metrics"
              summaryCollapsed={detailsSectionSummary}
              expanded={detailsSectionOpen}
              onToggle={() => setDetailsSectionOpen((o) => !o)}
              mb={0}
            >
              {!selectedExecutionId && (
                <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                  Select an execution in the table above.
                </Typography>
              )}

              {selectedExecutionId && detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 4 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading execution…
                  </Typography>
                </Box>
              )}

              {selectedExecutionId && !detailLoading && executionDetail && (
                <Grid container spacing={3} alignItems="stretch">
                  {showExecutionSummaryPanel && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          backgroundColor: alpha(theme.palette.background.paper, 0.6),
                          backdropFilter: 'blur(12px)',
                          border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.2)}`,
                          borderRadius: 2,
                          overflow: 'hidden',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Tooltip title="Hide summary panel" arrow>
                              <IconButton
                                onClick={toggleExecutionSummaryPanel}
                                aria-label="Hide summary panel"
                                sx={{
                                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                                  borderRadius: '10px',
                                  p: 1,
                                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28),
                                  },
                                }}
                              >
                                <NotesIcon sx={{ fontSize: '1.15rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: '"JetBrains Mono", monospace',
                                  fontWeight: 600,
                                  color: 'text.primary',
                                }}
                              >
                                Summary
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', md: 'row' },
                              justifyContent: 'space-between',
                              alignItems: { xs: 'stretch', md: 'flex-start' },
                              gap: { xs: 2.5, md: 3 },
                            }}
                          >
                            {/* Left: status + timeline */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1.25,
                                rowGap: 1,
                                minWidth: 0,
                              }}
                            >
                              <Chip
                                label={executionDetail.status}
                                size="small"
                                color={getStatusColor(executionDetail.status)}
                                variant="filled"
                                aria-label={`Execution status: ${executionDetail.status}`}
                              />
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ display: { xs: 'none', sm: 'block' }, borderColor: alpha(theme.palette.divider, 0.9) }}
                              />
                              <Tooltip title="Started">
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    fontFamily: '"Space Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: 'text.primary',
                                  }}
                                >
                                  {formatUiDateTime(executionDetail.started_at, timezone) || '—'}
                                </Typography>
                              </Tooltip>
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ display: { xs: 'none', sm: 'block' }, borderColor: alpha(theme.palette.divider, 0.9) }}
                              />
                              <Tooltip title="Completed">
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    fontFamily: '"Space Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: 'text.primary',
                                  }}
                                >
                                  {formatUiDateTime(executionDetail.completed_at, timezone) || '—'}
                                </Typography>
                              </Tooltip>
                            </Box>

                            {/* Right: impact bots + configuration snapshot */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                alignItems: { xs: 'stretch', md: 'flex-end' },
                                flexShrink: 0,
                                minWidth: { md: 280 },
                                maxWidth: { md: 420 },
                                width: { xs: '100%', md: 'auto' },
                              }}
                            >
                              <Box sx={{ width: '100%' }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: '"JetBrains Mono", monospace', display: 'block', mb: 0.75 }}
                                >
                                  Impact bots
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={impactBotsEdit}
                                    onChange={(e) => setImpactBotsEdit(e.target.value)}
                                    placeholder="—"
                                    inputProps={{ min: 0, step: 1 }}
                                    sx={{ width: 120 }}
                                  />
                                  <Button size="small" variant="contained" onClick={handleSaveImpactBots} disabled={savingImpactBots}>
                                    {savingImpactBots ? 'Saving…' : 'Update'}
                                  </Button>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: { md: 'right' } }}>
                                  Updates are recorded in the Audit Log.
                                </Typography>
                              </Box>
                              {hasResourceCaps(executionDetail.scenario) && (
                                <Box sx={{ width: '100%', textAlign: { md: 'right' } }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: '"JetBrains Mono", monospace', display: 'block', mb: 0.5 }}
                                  >
                                    Resource caps (per bot)
                                  </Typography>
                                  {describeResourceCaps(executionDetail.scenario, executionDetail.impact_bots ?? undefined).map((line) => (
                                    <Typography
                                      key={line}
                                      variant="caption"
                                      display="block"
                                      sx={{ fontFamily: '"Space Mono", monospace', color: theme.palette.warning.main }}
                                    >
                                      {line}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                              <Box sx={{ width: '100%' }}>
                                <Button
                                  size="small"
                                  onClick={() => setConfigSnapshotExpanded((e) => !e)}
                                  sx={{ textTransform: 'none', alignSelf: { md: 'flex-end' }, display: 'block', ml: { md: 'auto' } }}
                                >
                                  Configuration (snapshot) {configSnapshotExpanded ? '(collapse)' : '(expand)'}
                                </Button>
                                {configSnapshotExpanded && (
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 1.5,
                                      mt: 0.75,
                                      maxHeight: 200,
                                      overflow: 'auto',
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      backgroundColor: alpha(theme.palette.background.default, 0.5),
                                    }}
                                  >
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {JSON.stringify(executionDetail.configuration ?? {}, null, 2)}
                                    </pre>
                                  </Paper>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {['completed', 'failed', 'stopped'].includes(executionDetail.status) && (
                    <Grid item xs={12}>
                      <ExecutionBotIpsPanel executionId={executionDetail.id} />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!showExecutionSummaryPanel && (
                          <Tooltip title="Show summary panel" arrow>
                            <IconButton
                              size="small"
                              onClick={toggleExecutionSummaryPanel}
                              aria-label="Show summary panel"
                              sx={{
                                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.4)}`,
                                color: theme.palette.primary.main,
                              }}
                            >
                              <NotesIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!showExecutionLogPanel && (
                          <Tooltip title="Show log panel" arrow>
                            <IconButton
                              size="small"
                              onClick={toggleExecutionLogPanel}
                              aria-label="Show log panel"
                              sx={{
                                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.4)}`,
                                color: theme.palette.primary.main,
                              }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!showExecutionMetricsPanel && (
                          <Tooltip title="Show metrics panel" arrow>
                            <IconButton
                              size="small"
                              onClick={toggleExecutionMetricsPanel}
                              aria-label="Show metrics panel"
                              sx={{
                                border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.35 : 0.4)}`,
                                color: theme.palette.secondary.main,
                              }}
                            >
                              <TimelineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {showExecutionLogPanel && showExecutionMetricsPanel && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: { xs: 'none', md: 'inline' } }}
                        >
                          Drag the handle between columns to resize
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      ref={splitContainerRef}
                      sx={{
                        width: '100%',
                        display: { xs: 'flex', md: 'grid' },
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 2, md: 0 },
                        gridTemplateColumns: {
                          md:
                            !showExecutionLogPanel || !showExecutionMetricsPanel
                              ? 'minmax(0, 1fr)'
                              : `${logMetricsSplitPct}% ${LOG_METRICS_DIVIDER_PX}px minmax(0, 1fr)`,
                        },
                        alignItems: 'stretch',
                        minHeight: { md: 360 },
                      }}
                    >
                      {showExecutionLogPanel && (
                        <Box
                          sx={{
                            minWidth: 0,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                    <Card
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minWidth: 0,
                        width: '100%',
                        minHeight: { xs: 280, md: 0 },
                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.2)}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: { xs: 'none', md: 1 },
                          minHeight: { xs: 240, md: 0 },
                          display: 'flex',
                          flexDirection: 'column',
                          p: 2,
                          '&:last-child': { pb: 2 },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 2,
                            flexShrink: 0,
                          }}
                        >
                          <Tooltip title="Hide log panel" arrow>
                            <IconButton
                              onClick={toggleExecutionLogPanel}
                              aria-label="Hide log panel"
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                                borderRadius: '10px',
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28),
                                },
                              }}
                            >
                              <HistoryIcon sx={{ fontSize: '1.15rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: '"JetBrains Mono", monospace',
                                fontWeight: 600,
                                color: 'text.primary',
                              }}
                            >
                              Execution log
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: '"Space Mono", monospace',
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                mt: 0.25,
                                opacity: 0.85,
                              }}
                            >
                              Event timeline for this run
                            </Typography>
                          </Box>
                        </Box>

                        <TableContainer
                          onMouseLeave={() => {
                            if (!cursor.isLocked) cursor.hoverLogRow(null);
                          }}
                          sx={{
                            flex: { xs: 'none', md: 1 },
                            minHeight: { xs: 200, md: 0 },
                            width: '100%',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18)}`,
                            backgroundColor: alpha(theme.palette.background.default, isDark ? 0.25 : 0.04),
                          }}
                        >
                          <Table
                            size="small"
                            stickyHeader
                            sx={{
                              /* Auto layout: first column width = max intrinsic width across all timestamp cells */
                              tableLayout: 'auto',
                              width: '100%',
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    width: '1%',
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'bottom',
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '0.65rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    color: 'text.secondary',
                                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.22)}`,
                                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.92 : 0.97),
                                  }}
                                >
                                  Timestamp
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: '100%',
                                    minWidth: 0,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '0.65rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    color: 'text.secondary',
                                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.22)}`,
                                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.92 : 0.97),
                                  }}
                                >
                                  Description
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {!executionDetail.execution_log || executionDetail.execution_log.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={2}>
                                    <Typography
                                      sx={{
                                        fontFamily: '"Space Mono", monospace',
                                        fontSize: '0.85rem',
                                        color: 'text.secondary',
                                      }}
                                    >
                                      No events recorded.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                (executionDetail.execution_log as ExecutionLogEntry[]).map((entry, idx) => {
                                  const uKey = logUserKey(entry);
                                  const av = userAvatarColors(uKey, isDark);
                                  const fullWhen = formatUiDateTime(entry.timestamp, timezone);
                                  const shortWhen = formatUiTimeWithSeconds(entry.timestamp, timezone);
                                  return (
                                    <TableRow
                                      key={idx}
                                      hover
                                      selected={cursor.selectedLogRowIndex === idx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const ms = parseExecutionLogTimestampMs(entry.timestamp);
                                        if (ms == null) return;
                                        cursor.selectLogRow(idx, ms);
                                      }}
                                      onMouseEnter={() => {
                                        if (cursor.isLocked) return;
                                        const ms = parseExecutionLogTimestampMs(entry.timestamp);
                                        if (ms != null) cursor.hoverLogRow(ms);
                                      }}
                                      sx={{
                                        '&:last-child td': { borderBottom: 0 },
                                        '&.Mui-selected': {
                                          backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1),
                                        },
                                        '&.Mui-selected:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.14),
                                        },
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.06 : 0.05),
                                        },
                                      }}
                                    >
                                      <TableCell
                                        sx={{
                                          verticalAlign: 'top',
                                          borderColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1),
                                          width: '1%',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            width: 'max-content',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              flexWrap: 'nowrap',
                                            }}
                                          >
                                            <Tooltip title={fullWhen} placement="top" arrow enterDelay={400}>
                                              <Typography
                                                component="span"
                                                sx={{
                                                  fontFamily: '"Space Mono", monospace',
                                                  fontSize: '0.85rem',
                                                  fontWeight: 600,
                                                  color: 'text.primary',
                                                  cursor: 'default',
                                                  borderBottom: `1px dotted ${alpha(theme.palette.text.secondary, 0.45)}`,
                                                }}
                                              >
                                                {shortWhen}
                                              </Typography>
                                            </Tooltip>
                                            <Tooltip title={logUserTooltip(entry)} placement="top" arrow enterDelay={400}>
                                              <Avatar
                                                sx={{
                                                  width: 28,
                                                  height: 28,
                                                  fontSize: '0.8rem',
                                                  fontFamily: '"JetBrains Mono", monospace',
                                                  fontWeight: 700,
                                                  bgcolor: av.bg,
                                                  color: av.fg,
                                                  border: `1px solid ${alpha(theme.palette.common.white, isDark ? 0.12 : 0.2)}`,
                                                  cursor: 'default',
                                                }}
                                                aria-label={logUserTooltip(entry)}
                                              >
                                                {logUserInitial(entry)}
                                              </Avatar>
                                            </Tooltip>
                                          </Box>
                                          <Chip
                                            label={formatLogEventType(entry.event_type)}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              fontFamily: '"JetBrains Mono", monospace',
                                              fontSize: '0.6rem',
                                              height: 'auto',
                                              maxWidth: 'none',
                                              alignSelf: 'flex-start',
                                              borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.4),
                                              color: theme.palette.primary.main,
                                              '& .MuiChip-label': {
                                                whiteSpace: 'nowrap',
                                                lineHeight: 1.25,
                                                py: 0.4,
                                                display: 'block',
                                              },
                                            }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          verticalAlign: 'top',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'anywhere',
                                          borderColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1),
                                          minWidth: 0,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontFamily: '"Space Mono", monospace',
                                            fontSize: '0.875rem',
                                            lineHeight: 1.45,
                                            color: 'text.primary',
                                          }}
                                        >
                                          {getLogEntryDescription(entry) || '—'}
                                        </Typography>
                                        {(entry.provider_name || entry.region_name) && (
                                          <Typography
                                            component="div"
                                            sx={{
                                              fontFamily: '"Space Mono", monospace',
                                              fontSize: '0.7rem',
                                              color: 'text.secondary',
                                              mt: 0.75,
                                              opacity: 0.9,
                                            }}
                                          >
                                            {[entry.provider_name, entry.region_name].filter(Boolean).join(' · ')}
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                        </Box>
                      )}

                      {showExecutionLogPanel && showExecutionMetricsPanel && (
                        <Box
                          role="separator"
                          aria-orientation="vertical"
                          aria-label="Resize execution log and metrics"
                          onMouseDown={onLogMetricsDividerDown}
                          sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
                            },
                          }}
                        >
                          <DragIndicatorIcon
                            sx={{
                              color: 'text.secondary',
                              fontSize: '1rem',
                              transform: 'rotate(90deg)',
                            }}
                          />
                        </Box>
                      )}

                      {showExecutionMetricsPanel && (
                        <Box
                          sx={{
                            minWidth: 0,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {!metricsRange ? (
                            <Alert severity="info">
                              Metrics need a recorded start time for this execution. Prometheus charts are unavailable
                              until started_at is set.
                            </Alert>
                          ) : (
                            <MetricsPanel
                              isCollapsed={false}
                              onToggle={() => {}}
                              tenantId={selectedTenant?.tenant_id ?? null}
                              scenarioId={executionDetail.scenario_id}
                              scenarioName={executionDetail.scenario?.name ?? selectedScenario.name}
                              attackClass={
                                executionDetail.scenario?.attack_type?.attack_class ??
                                selectedScenario.attack_type?.attack_class
                              }
                              fixedRangeSec={metricsRange}
                              variant="embedded"
                              configurablePanels
                              syncReferenceTimeMs={cursor.activeReferenceMs}
                              onSelectReferenceTime={cursor.selectChartPoint}
                              referenceLocked={cursor.isLocked}
                              onEmbeddedAvatarClick={toggleExecutionMetricsPanel}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Grid>


                </Grid>
              )}
            </ExecutionsCollapsibleSection>
          </>
        )}
      </Box>
    </PageTransition>
  );
};

export default Executions;
